import { useCallback, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { useIsStripeConnected } from '@elfsight/lib-stripe-integration/widget';
import {
  FormProvider,
  WidgetProvider
} from '@elfsight/widget-form-builder/widget';
import { Popup } from '@elfsight/widget-modal';
import {
  WidgetBackground,
  type WidgetBackgroundProps
} from '@elfsight/widget-ui';
import {
  createReadableClass,
  getColorWithAlpha,
  getInvertedColor,
  scrollbarColoredMixin
} from '@elfsight/widget-utils';
import { useLingui } from '@lingui/react/macro';
import { useSettings } from '@wu/widget-core';
import { Checkout } from './checkout';
import { Form } from './form';
import { useFormPopup } from './form-popup-context';

// TODO: Перенести компонент в widget-form-builder
const POPUP_WIDTH = 620;
const SCROLLBAR_WIDTH = 10;

export const FormPopup = () => {
  const { t } = useLingui();

  const {
    widgetId = '1',
    font: { value: fontValue }
  } = useSettings();

  const [hasScroll, setHasScroll] = useState(false);
  const scrollableRef = useRef<HTMLDivElement | null>(null);

  const { isPopupOpen, closePopup } = useFormPopup();

  const checkScroll = useCallback(() => {
    requestAnimationFrame(() => {
      if (scrollableRef.current) {
        const hasVerticalScroll =
          scrollableRef.current.scrollHeight >
          scrollableRef.current.clientHeight;

        setHasScroll(hasVerticalScroll);
      }
    });
  }, []);

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);

    return () => {
      window.removeEventListener('resize', checkScroll);
    };
  }, [checkScroll, isPopupOpen]);

  const isStripeConnected = useIsStripeConnected();
  if (isStripeConnected) {
    return <Checkout />;
  }

  return (
    <WidgetProvider>
      <FormProvider widgetId={widgetId}>
        <StyledPopup
          show={isPopupOpen}
          ariaLabel={t`Form Popup`}
          onCloseRequest={closePopup}
          $font={fontValue}
          closeOnOutsideClick
          withoutTransition
          withCloseControl
          closeOnEscape
          disableScroll
          withBackdrop
          fixed
        >
          <ScrollableWrapper ref={scrollableRef}>
            <StyledWidgetBackground $hasScroll={hasScroll}>
              <Form />
            </StyledWidgetBackground>
          </ScrollableWrapper>
        </StyledPopup>
      </FormProvider>
    </WidgetProvider>
  );
};

interface StyledPopupProps {
  $font?: string;
}
const StyledPopup = styled(Popup).attrs<StyledPopupProps>(
  ({
    theme: {
      popup: { fullscreen }
    }
  }) => ({
    className: createReadableClass({ component: 'form-popup' }),
    closeControlColor: 'rgba(17, 17, 17, 0.9)',
    fullscreen
  })
)`
  position: absolute;
  overflow: hidden;
  box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.3);
  display: block;
  width: 100%;
  max-width: ${POPUP_WIDTH}px;
  font-family: ${({ $font }) => $font && `"${$font}"`};

  border-radius: ${({
    theme: {
      popup: { borderRadius }
    }
  }) => borderRadius}px;
  margin: ${({
    theme: {
      popup: { margin }
    }
  }) => margin}px;
`;

const ScrollableWrapper = styled.div.attrs({
  className: createReadableClass({
    component: 'form-popup',
    element: 'scrollable-wrapper'
  })
})`
  position: relative;
  overflow: hidden auto;
  height: 100%;

  ${({
    theme: {
      popup: { background }
    }
  }) => {
    const { color } = background ?? {};
    if (!color) {
      return;
    }

    const scrollbarColor = getInvertedColor(color);

    return css`
      @supports not selector(-webkit-scrollbar) {
        scrollbar-width: thin;
        scrollbar-color: ${getColorWithAlpha(scrollbarColor, 0.1)} transparent;
      }

      ${scrollbarColoredMixin(getColorWithAlpha(scrollbarColor, 0.1))};

      &::-webkit-scrollbar-thumb:hover {
        background-color: ${getColorWithAlpha(scrollbarColor, 0.3)};
      }

      &::-webkit-scrollbar-track {
        background-color: ${color};
      }
    `;
  }}
`;

interface StyledWidgetBackgroundProps extends Partial<WidgetBackgroundProps> {
  $hasScroll: boolean;
}

const StyledWidgetBackground = styled(
  WidgetBackground
).attrs<StyledWidgetBackgroundProps>(
  ({
    $hasScroll,
    theme: {
      popup: { paddingHorizontal = 0, paddingVertical, background }
    }
  }) => ({
    className: createReadableClass({
      component: 'form-popup',
      element: 'widget-background'
    }),
    contentStyle: {
      paddingTop: paddingVertical,
      paddingBottom: paddingVertical,
      paddingLeft: paddingHorizontal,
      paddingRight: $hasScroll
        ? paddingHorizontal - SCROLLBAR_WIDTH
        : paddingHorizontal
    },
    ...background
  })
)`
  height: 100%;
  max-height: ${({
    theme: {
      popup: { margin = 0 }
    }
  }) => `calc(100vh - ${margin * 2}px)`};
`;
